<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[105] flex items-center justify-center bg-black/40  px-4 py-6" @keydown.esc="$emit('cancel')">
      <div class="w-full max-w-sm flex flex-col bg-[hsl(var(--card))] rounded-md shadow-2xl overflow-hidden">
        <div class="flex items-center gap-2 p-3.5 border-b border-[hsl(var(--border)/0.6)] shrink-0">
          <p class="flex-1 text-[15px] font-semibold tracking-tight truncate">{{ t('map.routeEditor.addWaypointDialog.title') }}</p>
          <button
            type="button"
            :aria-label="t('map.routeEditor.saveDialog.cancel')"
            class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
            @click="$emit('cancel')"
          ><X class="w-4 h-4" /></button>
        </div>

        <div class="px-3.5 py-3.5 space-y-3">
          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">{{ t('map.routeEditor.waypointType') }}</span>
            <div class="relative">
              <select
                v-model="poiType"
                class="w-full h-10 pl-3 pr-7 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none appearance-none cursor-pointer"
              >
                <option v-for="type in EDITOR_POI_TYPES" :key="type" :value="type">{{ t(`poiTypes.${type}`) }}</option>
                <option value="marker">{{ t('map.routeEditor.addWaypointDialog.typeMarker') }}</option>
              </select>
              <ChevronDown class="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] pointer-events-none" />
            </div>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">{{ t('map.routeEditor.waypointName') }}</span>
            <input
              v-model="name"
              type="text"
              class="w-full h-10 px-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none"
            />
          </label>

          <label v-if="!isMarker" class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">{{ t('map.routeEditor.waypointDescription') }}</span>
            <textarea
              v-model="description"
              rows="2"
              class="w-full px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none resize-none"
            />
          </label>
        </div>

        <div class="flex items-center gap-2 px-3.5 py-3 border-t border-[hsl(var(--border)/0.6)] shrink-0">
          <button
            type="button"
            class="h-10 px-3 rounded-md bg-[hsl(var(--muted))] text-[12.5px] font-semibold hover:bg-[hsl(var(--muted)/0.8)] transition-colors"
            @click="$emit('cancel')"
          >{{ t('map.routeEditor.saveDialog.cancel') }}</button>
          <button
            type="button"
            class="flex-1 h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors"
            @click="confirm"
          >{{ t('map.routeEditor.addWaypointDialog.confirm') }}</button>
        </div>
        <div v-if="isMarker" class="px-3.5 pb-3 -mt-1 text-[11.5px] text-[hsl(var(--muted-foreground))]">
          {{ t('map.routeEditor.addWaypointDialog.markerHint') }}
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { X, ChevronDown } from "lucide-vue-next";
import { EDITOR_POI_TYPES, type PoiType } from "../types/poiTypes";

const props = defineProps<{
  initialType: PoiType;
}>();

const emit = defineEmits<{
  (e: "cancel"): void;
  (e: "confirm", poiType: PoiType, name: string, description: string | null): void;
  // «Метка» - личная заметка на карте (Мои метки), а не точка маршрута.
  (e: "confirm-marker", name: string): void;
}>();

const { t } = useI18n();

type DialogType = PoiType | "marker";
const poiType = ref<DialogType>(props.initialType);
const isMarker = computed(() => poiType.value === "marker");
const labelFor = (type: DialogType) => (type === "marker" ? t("map.routeEditor.addWaypointDialog.typeMarker") : t(`poiTypes.${type}`));
const name = ref(labelFor(props.initialType));
const description = ref("");

// Название по умолчанию следует за типом, пока пользователь не тронул поле
// руками - тот же приём, что и в остальных формах этого редактора.
let nameTouched = false;
watch(name, () => {
  nameTouched = true;
});
watch(poiType, (next) => {
  if (!nameTouched) name.value = labelFor(next);
});

const confirm = () => {
  const finalName = name.value.trim() || labelFor(poiType.value);
  if (poiType.value === "marker") {
    emit("confirm-marker", finalName);
    return;
  }
  emit("confirm", poiType.value, finalName, description.value.trim() || null);
};
</script>
